import { z } from "zod";

import { ALIAS_KINDS, LIMITS } from "../constants.js";
import {
  domainIdSchema,
  emailAddressSchema,
  isoUtcSchema,
  mailboxIdSchema,
  pageInfoSchema,
} from "./common.js";

export const mailboxKindSchema = z.enum(["personal", "shared"]);
export type MailboxKind = z.infer<typeof mailboxKindSchema>;

export const domainStatusSchema = z.enum(["pending", "verified", "failed"]);
export type DomainStatus = z.infer<typeof domainStatusSchema>;

export const aliasKindSchema = z.enum([...ALIAS_KINDS]);

/* -------------------------------------------------------------------------- */
/* Domains                                                                     */
/* -------------------------------------------------------------------------- */

export const domainSchema = z.object({
  id: domainIdSchema,
  name: z.string().min(3).max(LIMITS.maxDomainLength),
  status: domainStatusSchema,
  dkimSelector: z.string().min(1).max(63),
  dkimPublicKey: z.string().nullable(),
  spfVerified: z.boolean(),
  dmarcVerified: z.boolean(),
  createdAt: isoUtcSchema,
});
export type Domain = z.infer<typeof domainSchema>;

/* -------------------------------------------------------------------------- */
/* Mailboxes                                                                   */
/* -------------------------------------------------------------------------- */

export const mailboxSchema = z.object({
  id: mailboxIdSchema,
  address: emailAddressSchema,
  displayName: z.string().min(1).max(LIMITS.maxDisplayNameLength),
  kind: mailboxKindSchema,
  domainId: domainIdSchema,
  ownerUserId: z.string().nullable(),
  quotaBytes: z.number().int().positive().nullable(),
  usedBytes: z.number().int().nonnegative(),
  createdAt: isoUtcSchema,
});
export type Mailbox = z.infer<typeof mailboxSchema>;

export const mailboxWithCountsSchema = mailboxSchema.extend({
  unreadCount: z.number().int().nonnegative(),
  messageCount: z.number().int().nonnegative(),
});
export type MailboxWithCounts = z.infer<typeof mailboxWithCountsSchema>;

export const listMailboxesResponseSchema = z.object({
  mailboxes: z.array(mailboxWithCountsSchema),
  pageInfo: pageInfoSchema,
});
export type ListMailboxesResponse = z.infer<typeof listMailboxesResponseSchema>;

export const createMailboxRequestSchema = z.object({
  localPart: z
    .string()
    .min(1)
    .max(LIMITS.maxLocalPartLength)
    .regex(
      /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/,
      "Lowercase letters, digits, dot, underscore, hyphen",
    ),
  domainId: domainIdSchema,
  displayName: z.string().min(1).max(LIMITS.maxDisplayNameLength),
  kind: mailboxKindSchema.default("personal"),
});
export type CreateMailboxRequest = z.infer<typeof createMailboxRequestSchema>;

export const updateMailboxRequestSchema = z.object({
  displayName: z.string().min(1).max(LIMITS.maxDisplayNameLength).optional(),
  quotaBytes: z.number().int().positive().optional(),
});
export type UpdateMailboxRequest = z.infer<typeof updateMailboxRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Aliases                                                                     */
/* -------------------------------------------------------------------------- */

export const aliasSchema = z.object({
  id: z.string().min(1),
  address: emailAddressSchema,
  targetMailboxId: mailboxIdSchema,
  kind: aliasKindSchema,
  enabled: z.boolean(),
  createdAt: isoUtcSchema,
});
export type Alias = z.infer<typeof aliasSchema>;

export const listAliasesResponseSchema = z.object({
  aliases: z.array(aliasSchema),
  pageInfo: pageInfoSchema,
});
export type ListAliasesResponse = z.infer<typeof listAliasesResponseSchema>;

export const createAliasRequestSchema = z.object({
  address: emailAddressSchema,
  targetMailboxId: mailboxIdSchema,
  kind: aliasKindSchema.default("alias"),
});
export type CreateAliasRequest = z.infer<typeof createAliasRequestSchema>;
